import React from 'react';
import { COMPANY_INFO } from './company';

function ClientBlock({ client }) {
  if (!client) return null;
  if (client.est_professionnel) {
    const titre = client.nom || client.nom_entreprise || '';
    const contactPrenom = client.prenom || client.contact_1_prenom || '';
    const contactNom = client.nom_entreprise || client.contact_1_nom || '';
    const contact = `${contactPrenom} ${contactNom}`.trim();
    return (
      <>
        <p><strong>{titre}</strong></p>
        {contact && <p>Contact : {contact}</p>}
        {client.adresse && <p>{client.adresse}</p>}
        {client.ville && <p>{client.code_postal || ''} {client.ville}</p>}
        {client.telephone && <p>Tél : {client.telephone}</p>}
      </>
    );
  }
  return (
    <>
      <p><strong>{client.nom} {client.prenom || ''}</strong></p>
      {client.adresse && <p>{client.adresse}</p>}
      {client.ville && <p>{client.code_postal || ''} {client.ville}</p>}
      {client.telephone && <p>Tél : {client.telephone}</p>}
    </>
  );
}

export function PrintDevis({ devis, affaire, client, version }) {
  const styles = `
    @page { size: A4; margin: 12mm; }
    @media print {
      body * { visibility: hidden; }
      #print-zone, #print-zone * { visibility: visible; }
      #print-zone { position: fixed; top: 0; left: 0; right: 0; width: auto; max-width: 100%; padding: 0; margin: 0; }
      .doc-title h2 { font-size: 18px !important; }
      .no-print { display: none !important; }
    }
    #print-zone { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; color: #1F2937; }
    .header { display: flex; justify-content: space-between; margin-bottom: 32px; border-bottom: 3px solid #1E3A5F; padding-bottom: 20px; }
    .company { flex: 1; min-width: 0; }
    .company h1 { margin: 0; font-size: 28px; color: #1E3A5F; }
    .company p { margin: 4px 0; font-size: 13px; color: #6B7280; }
    .doc-title { text-align: right; flex-shrink: 0; }
    .doc-title h2 { margin: 0; font-size: 22px; color: #7A4E2D; text-transform: uppercase; white-space: nowrap; }
    .doc-title p { margin: 4px 0; font-size: 13px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 28px; }
    .info-box { background: #F7F8FA; padding: 16px; border-radius: 8px; border-left: 4px solid #1E3A5F; }
    .info-box h4 { margin: 0 0 8px 0; color: #1E3A5F; font-size: 12px; text-transform: uppercase; }
    .info-box p { margin: 2px 0; font-size: 13px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    th { background: #1E3A5F; color: white; padding: 10px 12px; text-align: left; font-size: 13px; }
    td { padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 13px; }
    tr:nth-child(even) { background: #F9FAFB; }
    .total-box { text-align: right; padding: 16px; background: #F0FDF4; border-radius: 8px; }
    .total-box .amount { font-size: 22px; font-weight: bold; color: #16A34A; }
    .tva-mention { font-size: 11px; color: #6B7280; margin-top: 4px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #E5E7EB; font-size: 11px; color: #9CA3AF; text-align: center; }
    .footer p { margin: 3px 0; }
    .signature-box { margin-top: 32px; border: 1px dashed #D1D5DB; padding: 20px; border-radius: 8px; }
    .notes-box { margin-top: 24px; padding: 16px; background: #FFFBEB; border-left: 4px solid #D97706; border-radius: 8px; }
    .notes-box h4 { margin: 0 0 8px 0; color: #92400E; font-size: 12px; text-transform: uppercase; }
    .notes-box .notes-content { font-size: 12px; color: #1F2937; white-space: pre-wrap; line-height: 1.5; }
  `;

  const totalHT = version?.lignes?.reduce((sum, l) => sum + parseFloat(l.total_ht||0), 0) || 0;

  return (
    <div id="print-zone">
      <style>{styles}</style>
      <div className="header">
        <div className="company">
          <h1>{COMPANY_INFO.name}</h1>
          <p>{COMPANY_INFO.description}</p>
          <p>{COMPANY_INFO.address}, {COMPANY_INFO.zipcode} {COMPANY_INFO.city}</p>
          <p>{COMPANY_INFO.phone} | {COMPANY_INFO.email}</p>
          <p>SIREN : {COMPANY_INFO.siren} | SIRET : {COMPANY_INFO.siret}</p>
        </div>
        <div className="doc-title">
          <h2>Devis</h2>
          <p><strong>N° {devis?.reference}</strong></p>
          <p>Date : {new Date().toLocaleDateString('fr-FR')}</p>
          {version?.date_echeance_validite && <p>Valable jusqu'au : {new Date(version.date_echeance_validite).toLocaleDateString('fr-FR')}</p>}
        </div>
      </div>
      <div className="info-grid">
        <div className="info-box">
          <h4>Client</h4>
          <ClientBlock client={client} />
        </div>
        <div className="info-box">
          <h4>Chantier</h4>
          <p><strong>{affaire?.titre}</strong></p>
          <p>Réf : {affaire?.reference}</p>
          {affaire?.ville_chantier && <p>{affaire.ville_chantier}</p>}
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th style={{width:'40%'}}>Désignation</th>
            <th>Unité</th><th>Qté</th><th>Prix HT</th><th>Total HT</th>
          </tr>
        </thead>
        <tbody>
          {version?.lignes?.map((l, i) => (
            <tr key={i}>
              <td>{l.designation}</td>
              <td>{l.unite||'-'}</td>
              <td>{l.quantite}</td>
              <td>{parseFloat(l.prix_unitaire_ht).toFixed(2)} €</td>
              <td><strong>{parseFloat(l.total_ht).toFixed(2)} €</strong></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="total-box">
        <div className="amount">Total HT : {totalHT.toFixed(2)} €</div>
        <div className="tva-mention">TVA non applicable, art. 293B du CGI</div>
      </div>
      {version?.notes && (
        <div className="notes-box">
          <h4>Conditions particulières</h4>
          <div className="notes-content">{version.notes}</div>
        </div>
      )}
      <div className="signature-box">
        <p style={{fontSize:'12px', color:'#6B7280', margin:'0 0 40px 0'}}>Bon pour accord — Signature et date du client :</p>
      </div>
      <div className="footer">
        <p>{COMPANY_INFO.name} — Auto-entrepreneur | SIREN : {COMPANY_INFO.siren} | SIRET : {COMPANY_INFO.siret} | {COMPANY_INFO.tva_mention}</p>
        <p>{COMPANY_INFO.late_penalty}</p>
        <p>{COMPANY_INFO.assurance_mention}</p>
      </div>
    </div>
  );
}

export function PrintFacture({ facture, affaire, client }) {
  const isAcompte = facture?.type === 'acompte';
  const couleur = isAcompte ? '#0369A1' : '#16A34A';
  const bgTotal = isAcompte ? '#EFF6FF' : '#F0FDF4';

  const styles = `
    @page { size: A4; margin: 12mm; }
    @media print {
      body * { visibility: hidden; }
      #print-zone, #print-zone * { visibility: visible; }
      #print-zone { position: fixed; top: 0; left: 0; right: 0; width: auto; max-width: 100%; padding: 0; margin: 0; }
      .doc-title h2 { font-size: 18px !important; }
      .no-print { display: none !important; }
    }
    #print-zone { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; color: #1F2937; }
    .header { display: flex; justify-content: space-between; margin-bottom: 32px; border-bottom: 3px solid ${couleur}; padding-bottom: 20px; }
    .company { flex: 1; min-width: 0; }
    .company h1 { margin: 0; font-size: 28px; color: #1E3A5F; }
    .company p { margin: 4px 0; font-size: 13px; color: #6B7280; }
    .doc-title { text-align: right; flex-shrink: 0; }
    .doc-title h2 { margin: 0; font-size: 22px; color: ${couleur}; text-transform: uppercase; white-space: nowrap; }
    .doc-title p { margin: 4px 0; font-size: 13px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 28px; }
    .info-box { background: #F7F8FA; padding: 16px; border-radius: 8px; border-left: 4px solid ${couleur}; }
    .info-box h4 { margin: 0 0 8px 0; color: ${couleur}; font-size: 12px; text-transform: uppercase; }
    .info-box p { margin: 2px 0; font-size: 13px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    th { background: ${couleur}; color: white; padding: 10px 12px; text-align: left; font-size: 13px; }
    td { padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 13px; }
    tr:nth-child(even) { background: #F9FAFB; }
    .total-box { text-align: right; padding: 16px; background: ${bgTotal}; border-radius: 8px; }
    .total-box .amount { font-size: 22px; font-weight: bold; color: ${couleur}; }
    .total-box .deduction { font-size: 14px; color: #6B7280; margin-bottom: 6px; }
    .total-box .net { font-size: 16px; font-weight: bold; color: #1E3A5F; margin-top: 6px; }
    .tva-mention { font-size: 11px; color: #6B7280; margin-top: 4px; }
    .acompte-ref-box { margin-bottom: 20px; padding: 12px 16px; background: #EFF6FF; border-radius: 8px; border-left: 4px solid #0369A1; font-size: 13px; color: #1E3A5F; }
    .paiement-box { margin-top: 20px; padding: 16px; background: #EFF6FF; border-radius: 8px; border-left: 4px solid #1E3A5F; }
    .paiement-box h4 { margin: 0 0 8px 0; color: #1E3A5F; font-size: 13px; }
    .paiement-box p { margin: 2px 0; font-size: 13px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #E5E7EB; font-size: 11px; color: #9CA3AF; text-align: center; }
    .footer p { margin: 3px 0; }
  `;

  const totalHT = parseFloat(facture?.montant_ht||0);
  const acomptesEncaisses = parseFloat(facture?.acomptes_encaisses||0);
  const netApresAcompte = totalHT - acomptesEncaisses;

  return (
    <div id="print-zone">
      <style>{styles}</style>
      <div className="header">
        <div className="company">
          <h1>{COMPANY_INFO.name}</h1>
          <p>{COMPANY_INFO.description}</p>
          <p>{COMPANY_INFO.address}, {COMPANY_INFO.zipcode} {COMPANY_INFO.city}</p>
          <p>{COMPANY_INFO.phone} | {COMPANY_INFO.email}</p>
          <p>SIREN : {COMPANY_INFO.siren} | SIRET : {COMPANY_INFO.siret}</p>
        </div>
        <div className="doc-title">
          <h2>{isAcompte ? 'Facture d\'acompte' : 'Facture'}</h2>
          <p><strong>N° {facture?.numero}</strong></p>
          <p>Date : {facture?.date_emission ? new Date(facture.date_emission).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR')}</p>
        </div>
      </div>

      {isAcompte && facture?.devis_id && (
        <div className="acompte-ref-box">
          📋 Acompte sur devis — Affaire : <strong>{affaire?.reference}</strong> – {affaire?.titre}
        </div>
      )}

      <div className="info-grid">
        <div className="info-box">
          <h4>Client</h4>
          <ClientBlock client={client} />
        </div>
        <div className="info-box">
          <h4>Chantier</h4>
          <p><strong>{affaire?.titre}</strong></p>
          <p>Réf : {affaire?.reference}</p>
          {affaire?.ville_chantier && <p>{affaire.ville_chantier}</p>}
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th style={{width:'40%'}}>Désignation</th>
            <th>Unité</th><th>Qté</th><th>Prix HT</th><th>Total HT</th>
          </tr>
        </thead>
        <tbody>
          {facture?.lignes?.map((l, i) => (
            <tr key={i}>
              <td>{l.designation}</td>
              <td>{l.unite||'-'}</td>
              <td>{l.quantite}</td>
              <td>{parseFloat(l.prix_unitaire_ht).toFixed(2)} €</td>
              <td><strong>{parseFloat(l.total_ht).toFixed(2)} €</strong></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="total-box">
        {isAcompte && facture?.montant_devis_total != null ? (
          <>
            <div className="deduction">Total devis HT : {parseFloat(facture.montant_devis_total).toFixed(2)} €</div>
            <div className="amount">Acompte facturé HT : {totalHT.toFixed(2)} €</div>
            {facture?.reste_a_facturer != null && (
              <div className="net">Reste à facturer HT : {parseFloat(facture.reste_a_facturer).toFixed(2)} €</div>
            )}
          </>
        ) : (
          <div className="amount">Total HT : {totalHT.toFixed(2)} €</div>
        )}
        {!isAcompte && acomptesEncaisses > 0 && (
          <>
            <div className="deduction">Déduction acompte(s) encaissé(s) : -{acomptesEncaisses.toFixed(2)} €</div>
            <div className="net">Net à payer HT : {netApresAcompte.toFixed(2)} €</div>
          </>
        )}
        <div className="tva-mention">TVA non applicable, art. 293B du CGI</div>
      </div>
      <div className="paiement-box">
        <h4>💳 Modalités de règlement</h4>
        <p>Paiement à réception par virement bancaire</p>
        <p>IBAN : {COMPANY_INFO.iban}</p>
        <p>BIC : {COMPANY_INFO.bic}</p>
      </div>
      <div className="footer">
        <p>{COMPANY_INFO.name} — Auto-entrepreneur | SIREN : {COMPANY_INFO.siren} | SIRET : {COMPANY_INFO.siret} | {COMPANY_INFO.tva_mention}</p>
        <p>{COMPANY_INFO.late_penalty}</p>
        <p>{COMPANY_INFO.assurance_mention}</p>
      </div>
    </div>
  );
}

export function PrintAvoir({ avoir, affaire, client }) {
  const styles = `
    @page { size: A4; margin: 12mm; }
    @media print {
      body * { visibility: hidden; }
      #print-zone, #print-zone * { visibility: visible; }
      #print-zone { position: fixed; top: 0; left: 0; right: 0; width: auto; max-width: 100%; padding: 0; margin: 0; }
      .doc-title h2 { font-size: 18px !important; }
      .no-print { display: none !important; }
    }
    #print-zone { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; color: #1F2937; }
    .header { display: flex; justify-content: space-between; margin-bottom: 32px; border-bottom: 3px solid #7C3AED; padding-bottom: 20px; }
    .company { flex: 1; min-width: 0; }
    .company h1 { margin: 0; font-size: 28px; color: #1E3A5F; }
    .company p { margin: 4px 0; font-size: 13px; color: #6B7280; }
    .doc-title { text-align: right; flex-shrink: 0; }
    .doc-title h2 { margin: 0; font-size: 22px; color: #7C3AED; text-transform: uppercase; white-space: nowrap; }
    .doc-title p { margin: 4px 0; font-size: 13px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 28px; }
    .info-box { background: #F7F8FA; padding: 16px; border-radius: 8px; border-left: 4px solid #7C3AED; }
    .info-box h4 { margin: 0 0 8px 0; color: #7C3AED; font-size: 12px; text-transform: uppercase; }
    .info-box p { margin: 2px 0; font-size: 13px; }
    .motif-box { background: #FDF4FF; padding: 14px 16px; border-radius: 8px; border-left: 4px solid #7C3AED; margin-bottom: 24px; font-size: 13px; }
    .motif-box strong { color: #7C3AED; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    th { background: #7C3AED; color: white; padding: 10px 12px; text-align: left; font-size: 13px; }
    td { padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 13px; }
    tr:nth-child(even) { background: #F9FAFB; }
    .total-box { text-align: right; padding: 16px; background: #FDF4FF; border-radius: 8px; }
    .total-box .amount { font-size: 22px; font-weight: bold; color: #7C3AED; }
    .tva-mention { font-size: 11px; color: #6B7280; margin-top: 4px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #E5E7EB; font-size: 11px; color: #9CA3AF; text-align: center; }
    .footer p { margin: 3px 0; }
  `;

  const totalHT = avoir?.lignes?.reduce((sum, l) => sum + parseFloat(l.total_ht||0), 0) || 0;

  return (
    <div id="print-zone">
      <style>{styles}</style>
      <div className="header">
        <div className="company">
          <h1>{COMPANY_INFO.name}</h1>
          <p>{COMPANY_INFO.description}</p>
          <p>{COMPANY_INFO.address}, {COMPANY_INFO.zipcode} {COMPANY_INFO.city}</p>
          <p>{COMPANY_INFO.phone} | {COMPANY_INFO.email}</p>
          <p>SIREN : {COMPANY_INFO.siren} | SIRET : {COMPANY_INFO.siret}</p>
        </div>
        <div className="doc-title">
          <h2>Avoir</h2>
          <p><strong>N° {avoir?.numero}</strong></p>
          <p>Date : {avoir?.date_emission ? new Date(avoir.date_emission).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR')}</p>
          <p style={{color:'#7C3AED'}}>Annule et remplace la facture N° {avoir?.numero_facture_origine}</p>
        </div>
      </div>
      <div className="info-grid">
        <div className="info-box">
          <h4>Client</h4>
          <ClientBlock client={client} />
        </div>
        <div className="info-box">
          <h4>Chantier</h4>
          <p><strong>{affaire?.titre}</strong></p>
          <p>Réf : {affaire?.reference}</p>
          {affaire?.ville_chantier && <p>{affaire.ville_chantier}</p>}
        </div>
      </div>
      <div className="motif-box">
        <strong>Motif de l'avoir : </strong>{avoir?.motif}
      </div>
      <table>
        <thead>
          <tr>
            <th style={{width:'40%'}}>Désignation</th>
            <th>Unité</th><th>Qté</th><th>Prix HT</th><th>Total HT</th>
          </tr>
        </thead>
        <tbody>
          {avoir?.lignes?.map((l, i) => (
            <tr key={i}>
              <td>{l.designation}</td>
              <td>{l.unite||'-'}</td>
              <td>{l.quantite}</td>
              <td>{parseFloat(l.prix_unitaire_ht).toFixed(2)} €</td>
              <td><strong>{parseFloat(l.total_ht).toFixed(2)} €</strong></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="total-box">
        <div className="amount">Total avoir HT : {totalHT.toFixed(2)} €</div>
        <div className="tva-mention">TVA non applicable, art. 293B du CGI</div>
      </div>
      <div className="footer">
        <p>{COMPANY_INFO.name} — Auto-entrepreneur | SIREN : {COMPANY_INFO.siren} | SIRET : {COMPANY_INFO.siret} | {COMPANY_INFO.tva_mention}</p>
        <p>Cet avoir annule et remplace la facture N° {avoir?.numero_facture_origine} pour le motif indiqué ci-dessus.</p>
        <p>{COMPANY_INFO.assurance_mention}</p>
      </div>
    </div>
  );
}

export function PrintLivreRecettes({ entrees, totaux, periode }) {
  const styles = `
    @page { size: A4 landscape; margin: 10mm; }
    @media print {
      body * { visibility: hidden; }
      #print-zone, #print-zone * { visibility: visible; }
      #print-zone { position: fixed; top: 0; left: 0; right: 0; width: auto; max-width: 100%; padding: 0; margin: 0; }
      .no-print { display: none !important; }
    }
    #print-zone { font-family: Arial, sans-serif; padding: 20px; color: #1F2937; font-size: 11px; }
    .lr-header { display: flex; justify-content: space-between; margin-bottom: 14px; border-bottom: 3px solid #1E3A5F; padding-bottom: 10px; }
    .lr-company h1 { margin: 0; font-size: 20px; color: #1E3A5F; }
    .lr-company p { margin: 2px 0; font-size: 10px; color: #6B7280; }
    .lr-title { text-align: right; }
    .lr-title h2 { margin: 0; font-size: 18px; color: #7A4E2D; text-transform: uppercase; }
    .lr-title p { margin: 2px 0; font-size: 11px; }
    .lr-table { width: 100%; border-collapse: collapse; margin-bottom: 14px; font-size: 9.5px; }
    .lr-table th { background: #1E3A5F; color: white; padding: 6px 8px; text-align: left; font-size: 10px; }
    .lr-table td { padding: 5px 8px; border-bottom: 1px solid #E5E7EB; vertical-align: top; }
    .lr-table tr:nth-child(even) td { background: #F9FAFB; }
    .lr-table .num { text-align: right; font-variant-numeric: tabular-nums; }
    .lr-recap { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin-top: 16px; page-break-inside: avoid; }
    .lr-recap-card { padding: 10px 12px; border-left: 4px solid #1E3A5F; background: #F9FAFB; border-radius: 4px; }
    .lr-recap-card.especes { border-left-color: #F59E0B; background: #FEF3C7; }
    .lr-recap-card.total { border-left-color: #16A34A; background: #DCFCE7; }
    .lr-recap-card .label { font-size: 9px; text-transform: uppercase; color: #6B7280; font-weight: 600; }
    .lr-recap-card .value { font-size: 13px; font-weight: 700; margin-top: 2px; font-variant-numeric: tabular-nums; }
    .lr-recap-card .sub { font-size: 9px; color: #9CA3AF; margin-top: 2px; }
    .lr-footer { margin-top: 16px; padding-top: 8px; border-top: 1px solid #E5E7EB; font-size: 9px; color: #9CA3AF; text-align: center; }
  `;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric' }) : '';
  const fmtMontant = (s) => parseFloat(s||0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  const labelPeriode = (() => {
    if (periode?.from && periode?.to) return `Du ${fmtDate(periode.from)} au ${fmtDate(periode.to)}`;
    if (periode?.from) return `À partir du ${fmtDate(periode.from)}`;
    if (periode?.to) return `Jusqu'au ${fmtDate(periode.to)}`;
    return 'Toute la période';
  })();

  const MODES = { virement: 'Virement', cheque: 'Chèque', especes: 'Espèces' };

  return (
    <div id="print-zone">
      <style>{styles}</style>
      <div className="lr-header">
        <div className="lr-company">
          <h1>{COMPANY_INFO.name}</h1>
          <p>{COMPANY_INFO.description}</p>
          <p>{COMPANY_INFO.address}, {COMPANY_INFO.zipcode} {COMPANY_INFO.city}</p>
          <p>SIRET : {COMPANY_INFO.siret}</p>
        </div>
        <div className="lr-title">
          <h2>Livre des recettes</h2>
          <p><strong>{labelPeriode}</strong></p>
          <p>Édité le {new Date().toLocaleDateString('fr-FR')}</p>
          <p style={{ fontSize:'9px', color:'#6B7280' }}>art. L123-28 CCom — BOI-BIC-DECLA-30-10-10</p>
        </div>
      </div>

      <table className="lr-table">
        <thead>
          <tr>
            <th style={{ width:'8%' }}>Date</th>
            <th style={{ width:'10%' }}>Type</th>
            <th style={{ width:'12%' }}>Pièce</th>
            <th style={{ width:'18%' }}>Client</th>
            <th style={{ width:'18%' }}>Affaire</th>
            <th style={{ width:'8%' }}>Mode</th>
            <th>Détails / Notes</th>
            <th style={{ width:'10%', textAlign:'right' }}>Montant</th>
          </tr>
        </thead>
        <tbody>
          {(entrees||[]).map((e, i) => {
            const detail = [];
            if (e.mode_paiement === 'cheque') {
              if (e.numero_cheque) detail.push(`N° ${e.numero_cheque}`);
              if (e.banque_cheque) detail.push(e.banque_cheque);
              if (e.tireur_cheque) detail.push(`Tireur : ${e.tireur_cheque}`);
            } else if (e.reference) {
              detail.push(`Réf : ${e.reference}`);
            }
            if (e.notes) detail.push(e.notes);
            if (e.registre_especes) detail.push('Registre espèces');
            const montant = parseFloat(e.montant);
            return (
              <tr key={i}>
                <td>{fmtDate(e.date)}</td>
                <td>{e.type === 'encaissement' ? 'Encaissement' : 'Remboursement'}</td>
                <td>
                  {e.piece?.type === 'facture' ? `F. ${e.piece.numero||''}` : `Av. ${e.piece?.numero||''}`}
                  {e.piece?.facture_origine_numero ? ` / F. ${e.piece.facture_origine_numero}` : ''}
                </td>
                <td>{e.client?.nom || '—'}</td>
                <td>{e.affaire?.intitule || ''}</td>
                <td>{MODES[e.mode_paiement] || e.mode_paiement}</td>
                <td style={{ fontSize:'9px' }}>{detail.join(' — ')}</td>
                <td className="num" style={{ color: montant >= 0 ? '#16A34A' : '#DC2626', fontWeight:'600' }}>
                  {fmtMontant(e.montant)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {totaux && (
        <div className="lr-recap">
          <div className="lr-recap-card total">
            <div className="label">Total général</div>
            <div className="value">{fmtMontant(totaux.total_general)}</div>
            <div className="sub">{totaux.nb_paiements} encais. / {totaux.nb_remboursements_avoirs} remb.</div>
          </div>
          {Object.entries(totaux.par_mode || {}).map(([mode, val]) => (
            <div key={mode} className="lr-recap-card">
              <div className="label">{MODES[mode] || mode}</div>
              <div className="value">{fmtMontant(val.montant)}</div>
              <div className="sub">{val.nb_operations} opération(s)</div>
            </div>
          ))}
          <div className="lr-recap-card especes">
            <div className="label">Registre espèces</div>
            <div className="value">{fmtMontant(totaux.registre_especes?.total)}</div>
            <div className="sub">{totaux.registre_especes?.nb_operations || 0} consignée(s)</div>
          </div>
        </div>
      )}

      <div className="lr-footer">
        <p>{COMPANY_INFO.name} — SIRET : {COMPANY_INFO.siret} — Régime micro-BIC, TVA non applicable art. 293B CGI</p>
        <p>Document édité automatiquement le {new Date().toLocaleString('fr-FR')}</p>
      </div>
    </div>
  );
}
